import React, { useState, useMemo, memo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Button, Box, Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

export default memo(function GenericSelector({
                                          label,
                                          rawOptions,
                                          getOptionLabel,
                                          industrieObjects,
                                          matchField,
                                          onChange,
                                          searchPlaceholder,
                                        }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState({});
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(5);

  const countMap = useMemo(() => {
    const map = {};
    industrieObjects.forEach((item) => {
      const fieldValue = item[matchField];
      if (Array.isArray(fieldValue)) {
        fieldValue.forEach(val => {
          const key = typeof val === "object" ? val.Name : val;
          if (key) map[key] = (map[key] || 0) + 1;
        });
      } else if (typeof fieldValue === "string") {
        map[fieldValue] = (map[fieldValue] || 0) + 1;
      }
    });
    return map;
  }, [industrieObjects, matchField]);

  const allOptions = useMemo(
    () =>
      rawOptions
        .map((title) => ({ title, count: countMap[title] || 0 }))
        .filter((option) => option.count > 0),
    [rawOptions, countMap]
  );

  const filteredOptions = useMemo(
    () =>
      allOptions.filter((option) =>
        option.title.toLowerCase().includes(search.toLowerCase())
      ),
    [allOptions, search]
  );

  const visibleOptions = filteredOptions.slice(0, visibleCount);

  const handleToggle = (title) => {
    const newChecked = { ...checked, [title]: !checked[title] };
    setChecked(newChecked);
    const selected = Object.keys(newChecked).filter((key) => newChecked[key]);
    onChange(selected);
  };

  const handleShowMore = () => setVisibleCount((prev) => prev + 5);

  const selectedCount = Object.values(checked).filter(Boolean).length;

  return (
    <Accordion
      disableGutters
      sx={{
        border: '1px solid #CCDBE6',
        borderRadius: '0 !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: '#0063a6',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#0063a6' }} />}
        sx={{
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
          '& .MuiAccordionSummary-content': { my: 0 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 0 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#313131' }}>
            {label}
          </Typography>
          {selectedCount > 0 && (
            <Chip
              label={selectedCount}
              size="small"
              sx={{
                backgroundColor: '#0063a6',
                color: '#fff',
                fontSize: '0.7rem',
                height: 20,
                minWidth: 20,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {allOptions.length > 5 && (
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVisibleCount(5);
            }}
            size="small"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                backgroundColor: '#EDF4F9',
                '& fieldset': { borderColor: '#CCDBE6' },
                '&:hover fieldset': { borderColor: '#0063a6' },
                '&.Mui-focused fieldset': { borderColor: '#0063a6', borderWidth: 2 },
              },
            }}
          />
        )}
        <FormGroup>
          {visibleOptions.map((option) => (
            <FormControlLabel
              key={option.title}
              control={
                <Checkbox
                  checked={!!checked[option.title]}
                  onChange={() => handleToggle(option.title)}
                  size="small"
                  sx={{
                    color: '#CCDBE6',
                    '&.Mui-checked': {
                      color: '#0063a6',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 99, 166, 0.04)',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {getOptionLabel(option)}
                  </Typography>
                  <Chip
                    label={option.count}
                    size="small"
                    sx={{
                      backgroundColor: '#EDF4F9',
                      fontSize: '0.7rem',
                      color: '#666',
                      height: 20,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              }
              sx={{
                mx: 0,
                borderRadius: 0,
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: '#EDF4F9',
                },
              }}
            />
          ))}
        </FormGroup>
        {visibleCount < filteredOptions.length && (
          <Button
            onClick={handleShowMore}
            size="small"
            sx={{
              mt: 1,
              color: '#0063a6',
              fontWeight: 600,
              fontSize: '0.8rem',
              '&:hover': {
                backgroundColor: 'rgba(0, 99, 166, 0.06)',
              },
            }}
          >
            {t("filters.show_more")}
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
});
